import { useState, useCallback } from 'react';
import { api } from '../api';

/**
 * Hook for handling file uploads and file management
 * @returns {Object} File upload functions and state
 */
const useFileUpload = () => {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [progress, setProgress] = useState(0);

  // Upload a file
  const uploadFile = useCallback(async (file, directory = '') => {
    setLoading(true);
    setError(null);
    setProgress(0);

    try {
      const formData = new FormData();
      formData.append('file', file);
      formData.append('directory', directory);

      const response = await api.post('/files/upload', formData, {
        headers: {
          'Content-Type': 'multipart/form-data',
        },
        onUploadProgress: (progressEvent) => {
          const percentCompleted = Math.round(
            (progressEvent.loaded * 100) / progressEvent.total
          );
          setProgress(percentCompleted);
        },
      });

      return response.data;
    } catch (err) {
      console.error('Error uploading file:', err);
      setError(err.message || 'Failed to upload file');
      return null;
    } finally {
      setLoading(false);
    }
  }, []);

  // Upload a property logo
  const uploadPropertyLogo = useCallback(async (file, propertyId, cityPath) => {
    setLoading(true);
    setError(null);
    setProgress(0);

    try {
      // Remove any leading or trailing slashes to prevent issues
      const normalizedCityPath = cityPath.replace(/^\/+|\/+$/g, '');
      
      const formData = new FormData();
      formData.append('file', file);
      formData.append('property_id', propertyId);
      formData.append('city_path', normalizedCityPath);

      const response = await api.post('/files/property-logo-upload', formData, {
        headers: {
          'Content-Type': 'multipart/form-data',
        },
        onUploadProgress: (progressEvent) => {
          const percentCompleted = Math.round(
            (progressEvent.loaded * 100) / progressEvent.total
          );
          setProgress(percentCompleted);
        },
      });

      return response.data;
    } catch (err) {
      console.error('Error uploading property logo:', err);
      setError(err.message || 'Failed to upload property logo');
      return null;
    } finally {
      setLoading(false);
    }
  }, []);

  // Upload a property floor plan
  const uploadPropertyFloorplan = useCallback(async (file, propertyId, cityPath) => {
    setLoading(true);
    setError(null);
    setProgress(0);

    try {
      // Remove any leading or trailing slashes to prevent issues
      const normalizedCityPath = cityPath.replace(/^\/+|\/+$/g, '');
      
      const formData = new FormData();
      formData.append('file', file);
      formData.append('property_id', propertyId);
      formData.append('city_path', normalizedCityPath);

      const response = await api.post('/files/property-floorplan-upload', formData, {
        headers: {
          'Content-Type': 'multipart/form-data',
        },
        onUploadProgress: (progressEvent) => {
          const percentCompleted = Math.round(
            (progressEvent.loaded * 100) / progressEvent.total
          );
          setProgress(percentCompleted);
        },
      });

      return response.data;
    } catch (err) {
      console.error('Error uploading property floor plan:', err);
      setError(err.message || 'Failed to upload property floor plan');
      return null;
    } finally {
      setLoading(false);
    }
  }, []);

  // Upload a property document
  const uploadPropertyDocument = useCallback(async (file, propertyId, cityPath, documentType) => {
    setLoading(true);
    setError(null);
    setProgress(0);

    try {
      // Remove any leading or trailing slashes to prevent issues
      const normalizedCityPath = cityPath.replace(/^\/+|\/+$/g, '');
      
      const formData = new FormData();
      formData.append('file', file);
      formData.append('property_id', propertyId);
      formData.append('city_path', normalizedCityPath);
      formData.append('document_type', documentType); // 'docs' or 'others'

      const response = await api.post('/files/property-document-upload', formData, {
        headers: {
          'Content-Type': 'multipart/form-data',
        },
        onUploadProgress: (progressEvent) => {
          const percentCompleted = Math.round(
            (progressEvent.loaded * 100) / progressEvent.total
          );
          setProgress(percentCompleted);
        },
      });

      return response.data;
    } catch (err) {
      console.error(`Error uploading property ${documentType}:`, err);
      setError(err.message || `Failed to upload property ${documentType}`);
      return null;
    } finally {
      setLoading(false);
    }
  }, []);

  // List files in a directory
  const listFiles = useCallback(async (directory = '') => {
    setLoading(true);
    setError(null);

    try {
      const response = await api.get('/files/', {
        params: { directory },
      });
      return response.data;
    } catch (err) {
      console.error('Error listing files:', err);
      setError(err.message || 'Failed to list files');
      return null;
    } finally {
      setLoading(false);
    }
  }, []);

  // Delete a file
  const deleteFile = useCallback(async (filePath) => {
    setLoading(true);
    setError(null);

    try {
      await api.delete(`/files/${filePath}`);
      return true;
    } catch (err) {
      console.error('Error deleting file:', err);
      setError(err.message || 'Failed to delete file');
      return false;
    } finally {
      setLoading(false);
    }
  }, []);

  // Delete a property file (logo, floorplan, documents, others)
  const deletePropertyFile = useCallback(async (propertyId, fileType, cityPath) => {
    setLoading(true);
    setError(null);

    try {
      // Remove any leading or trailing slashes from cityPath to prevent issues
      const normalizedCityPath = cityPath.replace(/^\/+|\/+$/g, '');
      
      console.log(`Deleting property file: /files/property-file/${propertyId}/${fileType}/${normalizedCityPath}`);
      
      await api.delete(`/files/property-file/${propertyId}/${fileType}/${normalizedCityPath}`);
      return true;
    } catch (err) {
      console.error(`Error deleting property ${fileType}:`, err);
      setError(err.message || `Failed to delete property ${fileType}`);
      return false;
    } finally {
      setLoading(false);
    }
  }, []);

  // Create a directory
  const createDirectory = useCallback(async (directory) => {
    setLoading(true);
    setError(null);

    try {
      const formData = new FormData();
      formData.append('directory', directory);

      const response = await api.post('/files/create-directory', formData);
      return response.data;
    } catch (err) {
      console.error('Error creating directory:', err);
      setError(err.message || 'Failed to create directory');
      return null;
    } finally {
      setLoading(false);
    }
  }, []);

  // Move a file
  const moveFile = useCallback(async (source, destination) => {
    setLoading(true);
    setError(null);

    try {
      const formData = new FormData();
      formData.append('source', source);
      formData.append('destination', destination);

      const response = await api.post('/files/move', formData);
      return response.data;
    } catch (err) {
      console.error('Error moving file:', err);
      setError(err.message || 'Failed to move file');
      return null;
    } finally {
      setLoading(false);
    }
  }, []);

  // Get file URL
  const getFileUrl = useCallback((filePath) => {
    if (!filePath) return '';
    
    // If filePath is a complete URL (starts with http), return it directly
    if (filePath.startsWith('http')) {
      return filePath;
    }
    
    // If filePath is a Google Drive link
    if (filePath.includes('drive.google.com')) {
      return filePath;
    }
    
    // If path starts with /static, use it with API base URL
    if (filePath.startsWith('/static/')) {
      // Ensure no double slashes in the URL - remove the leading slash for proper URL joining
      const cleanPath = filePath.replace(/^\/static/, '');
      return `${api.defaults.baseURL}/static${cleanPath}`;
    }
    
    // For other paths that might be full paths in the static directory
    if (filePath.startsWith('/')) {
      // Ensure proper URL joining
      const cleanPath = filePath.startsWith('/static/') 
        ? filePath.replace(/^\/static/, '') 
        : filePath;
      return `${api.defaults.baseURL}${cleanPath.startsWith('/') ? cleanPath : `/${cleanPath}`}`;
    }
    
    // For other relative paths (shouldn't happen in normal operation)
    return `${api.defaults.baseURL}/${filePath}`;
  }, []);

  return {
    loading,
    error,
    progress,
    uploadFile,
    uploadPropertyLogo,
    uploadPropertyFloorplan,
    uploadPropertyDocument,
    listFiles,
    deleteFile,
    deletePropertyFile,
    createDirectory,
    moveFile,
    getFileUrl,
  };
};

export default useFileUpload; 